import { useParams } from 'react-router-dom';
import { Api } from 'api';
import {
  Benchmark,
  PortfolioAnalyticsCalendar,
  TimeWindow,
} from 'types/Benchmark';
import { useEffect } from 'react';

interface ReturnProps<T> {
  benchmark: T;
  refetch: () => void;
  isLoading?: boolean;
}

export function useBenchmarkDetails<T>(): ReturnProps<T> {
  const { typeId } = useParams<{ typeId: string }>();

  const refetch = () => {
    refetchBenchmark();
    refetchCalendars();
    refetchAdminSettings();
  };

  const {
    data: benchmark,
    refetch: refetchBenchmark,
    isLoading: isLoadingBenchmarks,
  } = Api.benchmarks.GetBenchmark<Benchmark>(typeId);

  const benchmarkData = benchmark?.data as Benchmark;
  let prevFromDate: string = '';
  benchmarkData?.timeWindows
    ?.sort((a, b) => b.fromDate?.localeCompare(a.fromDate))
    .map((timeWindow: TimeWindow) => {
      if (timeWindow) {
        timeWindow.toDate = prevFromDate;
        prevFromDate = timeWindow?.fromDate;
      }

      return timeWindow;
    });

  const {
    data: calendars,
    refetch: refetchCalendars,
    isLoading: isLoadingCalendars,
  } = Api.lookup.GetCalendars();

  useEffect(() => {
    if (benchmark?.data && benchmark.data.calculationFrequency) {
      const calculationFrequencyId = benchmark.data.calculationFrequency.id;
      benchmark.data.calculationFrequencyId = calculationFrequencyId;
    }

    if (benchmark?.data && calendars) {
      const benchmarkProps = benchmark.data as Benchmark;

      const calendar = calendars.filter(
        (x: PortfolioAnalyticsCalendar) =>
          x.id === benchmarkProps.portfolioAnalyticsCalendarId
      );

      if (calendar) {
        (benchmark.data as Benchmark).portfolioAnalyticsCalendar = calendar[0];
      }
    }
  }, [benchmark, calendars]);

  const {
    data: adminSettings,
    refetch: refetchAdminSettings,
    isLoading: isLoadingAdminSettings,
  } = Api.admin.GetAdminSettings();

  useEffect(() => {
    if (adminSettings) {
      if (adminSettings.defaultLagDate) {
        if (benchmark) {
          (benchmark.data as Benchmark).defaultLagDate =
            adminSettings.defaultLagDate;
        }
      }
    }
  }, [benchmark, adminSettings]);

  return {
    benchmark: benchmark?.data as unknown as T,
    refetch,
    isLoading:
      isLoadingBenchmarks || isLoadingCalendars || isLoadingAdminSettings,
  };
}
